import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  CallToActionButton,
  Spacer,
  StandByOverlay,
  TextCaption
} from '@directsoftware/ui-kit-web-admin';
import { toast } from 'react-toastify';
import { postWlsVerify } from '../../../utils/wls-api-client';
import { selectVerificationState } from '../../../redux/slices/verification';

const VerificationSubmit = () => {
  const brand = useSelector(state => state.brand);
  const verificationState = useSelector(selectVerificationState);
  const [showOverlay, setShowOverlay] = useState(false);

  const hasValidSignature = () => {
    if (verificationState.verify_signature) {
      return verificationState.signature !== null;
    }
    return true;
  };

  const hasValidIdPhoto = () => {
    if (verificationState.verify_id) {
      return verificationState.idPhoto !== null;
    }
    return true;
  };

  const hasValidTerms = () => {
    const terms = verificationState.contractTermsAndConditions;

    if (!terms) {
      return true;
    }

    if (
      terms.replace(/<(?:.|\n)*?>/gm, '').replace(/(\r\n|\n|\r)/gm, '') !== ''
    ) {
      return verificationState.terms_accepted;
    }

    return true;
  };

  const hasValidAge = () => {
    if (verificationState.verify_age) {
      return verificationState.age_verified;
    }

    return true;
  };

  const hasValidAddress = () => {
    if (verificationState.verify_address) {
      return verificationState.address_verified;
    }

    return true;
  };

  const hasInvalidFields = () => {
    return !(
      hasValidSignature() &&
      hasValidIdPhoto() &&
      hasValidAge() &&
      hasValidAddress() &&
      hasValidTerms()
    );
  };

  const postData = () => {
    const data = {
      booking_code: verificationState.booking.booking_code,
      booking_id: verificationState.booking.id
    };
    if (verificationState.signature) {
      data.signature_id = verificationState.signature.id;
    }

    if (verificationState.idPhoto) {
      data.id_photo_id = verificationState.idPhoto.id;
    }

    if (verificationState.address_verified) {
      data.location = {
        adr_street: verificationState.adrStreet,
        adr_unit: verificationState.adrUnit,
        adr_city: verificationState.adrCity,
        adr_state: verificationState.adrState,
        adr_country: verificationState.adrCountry,
        adr_postal_code: verificationState.adrPostalCode
      };
    }

    if (verificationState.age_verified) {
      data.age_verified = verificationState.age_verified;
    }

    return data;
  };

  const submitVerification = () => {
    setShowOverlay(true);
    postWlsVerify(brand, verificationState.customer.id, postData())
      .then(response => {
        setShowOverlay(false);
        window.location = `/my-bookings/receipt/${
          verificationState.booking.booking_code
        }?verified=true`;
      })
      .catch(error => {
        toast.error(error);
        console.log(error);
        setShowOverlay(false);
      });
  };

  return (
    <>
      <StandByOverlay
        reveal={showOverlay}
        position="fixed"
        headline="Verifying your info..."
      />
      <CallToActionButton
        onClick={() => submitVerification()}
        isFullWidth
        size="large"
        isDisabled={hasInvalidFields() || showOverlay}
      >
        Submit Verification
      </CallToActionButton>
      <Spacer size="m" />
      <TextCaption>
        By submitting the form above, you agree to this listing’s
        {` `}
        <a
          href={`/listings/${verificationState.listing.slug}#details-rules`}
          target="_blank"
          rel="noreferrer"
        >
          terms and conditions
        </a>
      </TextCaption>
      {verificationState?.rentalAgreement && (
        <>
          <Spacer />
          <TextCaption>
            By submitting the form above, you agree to abide by the terms and
            conditions put forth in the
            {` `}
            <a
              href={verificationState?.rentalAgreement?.pdf?.url}
              target="_blank"
              rel="noreferrer"
            >
              rental agreement
            </a>
          </TextCaption>
        </>
      )}
    </>
  );
};

export default VerificationSubmit;
